<script setup lang="ts">
import { FeatureRole, OrganizationRole } from '@/enums/roles';
import { useOrganizationEmployeeStore } from '@/stores/organizationEmployee';
import { EmployeeStatus, type OrganizationEmployee } from '@/types/employee';
import type { UpsertOrganizationEmployeesRequest } from '@/types/requests/organizationEmployees/upsertOrganizationEmployeesRequest';
import { getAvailableRoles } from '@/utils/organization';
import { getEnumNameFromRole, getRoleFromFeatures } from '@/utils/role';
import { required } from '@/utils/validation';
import { computedAsync } from '@vueuse/core';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t: translate } = useI18n();

const organizationEmployeeStore = useOrganizationEmployeeStore();

const props = defineProps<{
    organizationEmployee?: OrganizationEmployee;
    organizationId: string;
}>();

const availableRoles = computedAsync(async () => await getAvailableRoles(props.organizationId), []);

const request = ref<UpsertOrganizationEmployeesRequest>({
    roles: [OrganizationRole.Employee],
    isReadOnly: false,
    status: EmployeeStatus.Active,
    employeeIds: []
});

async function submit(employeeIds: string[]): Promise<void> {
    request.value.employeeIds = employeeIds;
    if (typeof request.value.roles === 'string') {
        request.value.roles = [request.value.roles];
    }

    await organizationEmployeeStore.upsertMany(props.organizationId, request.value);
}

watch(
    () => props.organizationEmployee,
    () => {
        if (!props.organizationEmployee) return;
        request.value = { ...props.organizationEmployee, employeeIds: [] };

        const featureRoles = getRoleFromFeatures(props.organizationEmployee.features);
        if (featureRoles && featureRoles.length > 0) {
            request.value.roles = featureRoles.filter((r) => !availableRoles.value.includes(r));
        }
    },
    { immediate: true }
);

watch(
    () => request.value.roles,
    () => {
        if (!request.value.roles?.includes(FeatureRole.ActionCoordinator)) return;
        request.value.isReadOnly = false;
    },
    { immediate: true }
);

defineExpose({ submit, request });
</script>

<template>
    <div class="d-flex align-center ga-3">
        <v-select
            label="Rol"
            v-model="request.roles"
            required
            :rules="[required]"
            :items="Object.entries(availableRoles)"
            :item-title="(entry) => translate(`enums.${getEnumNameFromRole(entry[1])}.${entry[1]}`)"
            :item-value="[1]"
        />
        <v-checkbox
            :disabled="request.roles?.includes(FeatureRole.ActionCoordinator)"
            v-model="request.isReadOnly"
            label="Kan alleen lezen"
            density="compact"
        />
    </div>
</template>

<style scoped></style>
